import gsap from "gsap";

const CONTENT_EASE = "power2.out";
const SWITCH_EASE = "none";

/**
 * Parse final numeric target from `data-stat-value` or current text (e.g. "12", "100%").
 * @param {HTMLElement} el
 * @returns {{ num: number, isPercent: boolean }}
 */
export function getStatTarget(el) {
  const raw = (
    el.getAttribute("data-stat-value") ||
    el.textContent ||
    ""
  ).trim();
  if (raw.includes("%")) {
    return {
      num: parseFloat(raw.replace("%", "").trim()) || 0,
      isPercent: true,
    };
  }
  return { num: parseFloat(raw) || 0, isPercent: false };
}

function formatStatValue(num, isPercent) {
  if (isPercent) {
    return `${Math.round(num)}%`;
  }
  return String(Math.round(num));
}

/**
 * Count-up tween for a single `.map-section__stat-value` node.
 * @param {HTMLElement} el
 */
export function tweenMapStatValue(el) {
  const { num, isPercent } = getStatTarget(el);
  const proxy = { v: 0 };
  return gsap.to(proxy, {
    v: num,
    duration: 1.05,
    ease: "none",
    onUpdate: () => {
      el.textContent = formatStatValue(proxy.v, isPercent);
    },
  });
}

/**
 * Resets stat display strings to zero (before count-up).
 * @param {NodeListOf<HTMLElement>|HTMLElement[]} statValues
 */
export function resetMapStatDisplays(statValues) {
  statValues.forEach((el) => {
    const { isPercent } = getStatTarget(el);
    el.textContent = isPercent ? "0%" : "0";
  });
}

/**
 * Left column: lead slide + fade, stats fade, count-up, then either notice (intro) or CTA (switch).
 *
 * @param {gsap.core.Timeline} timeline
 * @param {HTMLElement | null} contentRoot — `.map-section__content`
 * @param {number} start — timeline position (seconds)
 * @param {object} [options]
 * @param {'intro'|'switch'} [options.variant='intro'] — intro: animate “Land auswählen”; switch: animate CTA, no notice
 * @param {string} [options.ease] — override default easing (use `none` for switch = linear motion)
 */
export function appendMapContentReveal(
  timeline,
  contentRoot,
  start = 0,
  options = {},
) {
  if (!contentRoot) {
    return;
  }

  const { variant = "intro", ease: easeOption } = options;
  const ease =
    easeOption ?? (variant === "switch" ? SWITCH_EASE : CONTENT_EASE);

  const subheadline = contentRoot.querySelector(".map-section__subheadline");
  const lead = contentRoot.querySelector(".map-section__lead");
  const statsRow = contentRoot.querySelector(".map-section__stats");
  const notice = contentRoot.querySelector(".map-section__notice");
  const cta = contentRoot.querySelector(".map-section__cta");
  const statValues = contentRoot.querySelectorAll(".map-section__stat-value");
  const previewImgs = contentRoot.querySelectorAll(
    ".map-section__previews .map-section__preview",
  );

  if (statsRow) {
    timeline.set(statsRow, { autoAlpha: 0 }, start);
  }
  if (variant === "intro" && notice) {
    timeline.set(notice, { autoAlpha: 0 }, start);
  }
  if (variant === "intro" && cta) {
    timeline.set(cta, { autoAlpha: 0 }, start);
  }
  if (variant === "switch" && cta) {
    timeline.set(cta, { autoAlpha: 0, x: -24 }, start);
  }

  if (statValues.length) {
    timeline.call(
      () => {
        resetMapStatDisplays(statValues);
      },
      null,
      start,
    );
  }

  if (previewImgs.length) {
    timeline.fromTo(
      previewImgs,
      { autoAlpha: 0 },
      {
        autoAlpha: 1,
        duration: 0.72,
        ease,
        stagger: 0.07,
      },
      start,
    );
  }

  const leadIntro = [subheadline, lead].filter(Boolean);
  if (leadIntro.length) {
    timeline.fromTo(
      leadIntro,
      { autoAlpha: 0, x: -40 },
      {
        autoAlpha: 1,
        x: 0,
        duration: 0.88,
        ease,
        force3D: true,
        stagger: subheadline && lead ? 0.07 : 0,
      },
      start,
    );
  }

  if (statsRow) {
    timeline.fromTo(
      statsRow,
      { autoAlpha: 0 },
      { autoAlpha: 1, duration: 0.5, ease },
      start + 0.12,
    );
  }

  if (statValues.length) {
    statValues.forEach((el, i) => {
      timeline.add(tweenMapStatValue(el), start + 0.16 + i * 0.07);
    });
  }

  if (variant === "intro" && notice) {
    timeline.fromTo(
      notice,
      { autoAlpha: 0, x: -32 },
      {
        autoAlpha: 1,
        x: 0,
        duration: 0.82,
        ease: CONTENT_EASE,
        force3D: true,
      },
      start + 0.52,
    );
  }

  if (variant === "switch" && cta) {
    timeline.fromTo(
      cta,
      { autoAlpha: 0, x: -24 },
      {
        autoAlpha: 1,
        x: 0,
        duration: 0.55,
        ease,
        force3D: true,
      },
      start + 0.52,
    );
  }
}
